"use client";

import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const milestones = [{
  year: "2008",
  title: "The First Roots",
  description: "VineEssence was born on a single sun-drenched slope in the heart of the valley. We planted our first Cabernet Sauvignon vines, rooted in a steadfast commitment to sustainable viticulture and artisanal excellence from day one."
}, {
  year: "2012",
  title: "The Inaugural Vintage",
  description: "After years of patience and meticulous care, we celebrated our first harvest. This inaugural vintage captured the unique character of our terroir, earning immediate acclaim for its depth and sophisticated profile."
}, {
  year: "2015",
  title: "The Gravity-Flow Cellar",
  description: "To honor the integrity of our fruit, we unveiled our state-of-the-art gravity-flow winery. This facility allows us to process grapes with the utmost gentleness, preserving the delicate essence of every cluster."
}, {
  year: "2018",
  title: "Sustainable Certification",
  description: "VineEssence officially achieved organic certification. This milestone reflects our deep respect for the land and our mission to produce premium wines that are as pure as the environment they come from."
}, {
  year: "2021",
  title: "The Estate Lounge",
  description: "We opened our doors to the public with an architecturally stunning tasting lounge. Designed to blend seamlessly into the landscape, it offers guests an immersive journey into the world of fine winemaking."
}, {
  year: "2024",
  title: "A Legacy of Excellence",
  description: "Today, VineEssence stands as a hallmark of premium viticulture. We continue to explore new varietals and innovative techniques while staying true to our heritage of quality and accessible luxury."
}];
interface TimelineItemProps {
  year: string;
  title: string;
  description: string;
  isLast: boolean;
  "data-index": number;
}
function TimelineItem({
  year,
  title,
  description,
  isLast,
  "data-index": dataIndex
}: TimelineItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3,
    once: true
  });
  return <div data-section-id="135263" ref={ref} data-index={dataIndex} className="relative flex flex-col md:flex-row gap-8 md:gap-16 py-12 md:py-20 group">
      {}
      <div className={cn("md:w-48 shrink-0 md:pr-6 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
        <h3 className="font-mono text-4xl md:text-5xl font-bold text-foreground md:text-right tracking-tighter group-hover:text-secondary transition-colors duration-500">
          {year}
        </h3>
      </div>

      {}
      <div className="absolute left-0 md:left-48 top-0 bottom-0 w-px flex flex-col items-center -translate-x-1/2 md:translate-x-0">
        {}
        
        {}
        <div className={cn("absolute top-16 md:top-24 w-4 h-4 rounded-full border-2 transition-all duration-700 ease-out z-10 -translate-x-1/2 md:translate-x-[-50%]", isVisible ? "bg-secondary border-secondary scale-100 shadow-[0_0_20px_rgba(184,153,77,0.3)]" : "bg-background border-muted-foreground scale-50 opacity-50")} />
      </div>

      {}
      <div className={cn("flex-1 pl-8 md:pl-0 transition-all duration-1000 delay-200 ease-out", isVisible ? "opacity-100 translate-y-0 blur-none" : "opacity-0 translate-y-12 blur-sm")}>
        <h4 className="text-2xl font-serif font-bold mb-4 text-foreground italic">{title}</h4>
        <p className="text-muted-foreground font-sans leading-relaxed text-lg max-w-2xl tracking-normal">
          {description}
        </p>
      </div>
    </div>;
}
export default function ADecadeOfArtistry() {
  return <section id="our-history" className="bg-background text-foreground py-24 sm:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 max-w-6xl">
        
        {}
        <div className="max-w-3xl mb-24">
          <h2 className="font-serif text-5xl md:text-7xl font-bold text-foreground mb-6 tracking-tighter">
            Our Heritage
          </h2>
          <p className="text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl">
            Discover the journey of VineEssence, from our first roots in the valley to our standing as a hallmark of winemaking excellence and accessible luxury.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-0 md:left-48 top-0 bottom-0 w-px bg-border -translate-x-1/2 md:translate-x-0" />

          {}
          <div className="flex flex-col">
            {milestones.map((milestone, index) => <TimelineItem key={milestone.year} data-index={index} year={milestone.year} title={milestone.title} description={milestone.description} isLast={index === milestones.length - 1} />)}
          </div>
        </div>

      </div>
    </section>;
}